import { useMemo } from 'react';
import { ArrowSquareOut, GitFork } from '@phosphor-icons/react';
import { RepositoryDocument } from 'common/github/RepositoryDocument';
import { trpc, useQuery } from 'client/lib/trpc';
import { Button } from 'client/components/Button';
import type { CatalogItemProps } from 'client/components/Catalog';
import { RepositoryTitle } from 'client/components/GitHub/RepositoryTitle';
import { Tag } from 'client/components/Tag';

export const createItemFromGithub = (
  { id, name, owner, language, image, isPrivate, isFork }: RepositoryDocument,
  identifier = ''
): CatalogItemProps => ({
  id: `${id}`,
  title: <RepositoryTitle name={name} isPrivate={isPrivate} />,
  subtitle: (
    <>
      {!!identifier && (
        <>
          <Tag>{identifier}</Tag>{' '}
        </>
      )}
      {[owner, language].filter((item) => !!item).join(', ')}
    </>
  ),
  image: {
    alt: `${owner} profile picture`,
    src: image,
  },
  ornamentIcon: isFork ? <GitFork size={16} /> : null,
  action: (
    <a href={`https://github.com/${name}`} target="_blank">
      <Button icon={<ArrowSquareOut />} />
    </a>
  ),
});

/** Helper to load Diploi repository data with ease */
export const useRepository = ({
  projectID,
  includeBranches = false,
  includeTags = false,
  enabled = true,
}: {
  projectID: number | null | undefined;
  includeBranches?: boolean;
  includeTags?: boolean;
  enabled?: boolean;
}) => {
  const isEnabled = enabled && !!projectID;

  const repositoryQuery = useQuery(
    trpc.github.repositoryLoad,
    { projectID: projectID as number, includeBranches, includeTags },
    {
      enabled: isEnabled,
      retry: false,
    }
  );

  const repositoryItems = useMemo(() => {
    if (!repositoryQuery.data?.repository) return [] as CatalogItemProps[];
    return [createItemFromGithub(repositoryQuery.data?.repository)];
  }, [repositoryQuery.data?.repository]);

  return { repository: repositoryQuery?.data?.repository || null, repositoryItems, isLoading: repositoryQuery.isLoading };
};
